import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, ExternalLink, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  ConfirmButton,
  HedgeTpslPanel,
  LeveragePanel,
  TpslPanel,
  opBtn,
} from "@/components/ExchangeOpsPanels";

const fmtNum = (n, d = 2) =>
  n != null && Number.isFinite(Number(n)) ? Number(n).toFixed(d) : "—";
const fmtSigned = (n) => {
  const v = Number(n || 0);
  return `${v >= 0 ? "+" : "-"}$${Math.abs(v).toFixed(2)}`;
};
const errMsg = (e) =>
  e?.response?.data?.detail?.message ||
  (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : null) ||
  e?.message ||
  "Hata";

/** Pozisyonları hedge çiftleri (Darkex + karşı borsa, aynı sembol) ve
 *  tekil bacaklar olarak grupla. */
function groupPositions(positions) {
  const bySymbol = {};
  positions.forEach((p) => {
    (bySymbol[p.symbol] = bySymbol[p.symbol] || []).push(p);
  });
  const pairs = [];
  const singles = [];
  Object.values(bySymbol).forEach((group) => {
    const dk = group.find((p) => p.exchange === "DARKEX");
    const counter = group.find((p) => p.exchange !== "DARKEX");
    if (dk && counter) {
      pairs.push({ dk, counter });
      group.forEach((p) => {
        if (p !== dk && p !== counter) singles.push(p);
      });
    } else {
      singles.push(...group);
    }
  });
  return { pairs, singles };
}

const exColor = (ex) => (ex === "DARKEX" ? "var(--acc-2)" : "var(--gold)");
const exLabel = (ex) =>
  ex === "DARKEX" ? "Darkex" : ex === "BINANCE" ? "Binance" : ex;

export default function ExchangePositions() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["exchange-positions"],
    queryFn: api.exchangePositions,
    refetchInterval: 10000,
    staleTime: 4000,
  });
  // Açık panel: { key: "SYMBOL-EXCHANGE" | "SYMBOL-pair", type: "tpsl"|"lev"|"hedge" }
  const [panel, setPanel] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["exchange-positions"] });

  const closeLeg = useMutation({
    mutationFn: api.exClose,
    onSuccess: (d) => {
      toast.success(`${exLabel(d.exchange)} pozisyonu kapatıldı ✅`);
      refresh();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const closePair = useMutation({
    mutationFn: api.exClosePair,
    onSuccess: (d) => {
      if (d.partial) {
        const bad = d.darkex?.error
          ? `Darkex: ${d.darkex.error}`
          : `Karşı borsa: ${d.counter?.error}`;
        toast.warning(`Bir bacak kapandı, diğeri HATA verdi — ${bad}`);
      } else {
        toast.success("Hedge çifti iki borsada aynı anda kapatıldı ✅");
      }
      refresh();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const tpsl = useMutation({
    mutationFn: api.exTpsl,
    onSuccess: (d) => {
      toast.success(`${exLabel(d.exchange)} TP/SL emirleri yerleşti ✅`);
      setPanel(null);
      refresh();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const hedgeTpsl = useMutation({
    mutationFn: api.exHedgeTpsl,
    onSuccess: (d) => {
      if (d.partial) {
        const bad = d.darkex_sl?.error
          ? `Darkex SL: ${d.darkex_sl.error}`
          : `Karşı TP: ${d.counter_tp?.error}`;
        toast.warning(`Bir emir yerleşti, diğeri HATA verdi — ${bad}`);
      } else {
        toast.success(`Hedge TP/SL kuruldu · tetik $${d.trigger_price} ✅`);
      }
      setPanel(null);
      refresh();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const leverage = useMutation({
    mutationFn: api.exLeverage,
    onSuccess: (d) => {
      toast.success(`${exLabel(d.exchange)} kaldıraç ${d.leverage}× yapıldı ✅`);
      setPanel(null);
      refresh();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const positions = data?.positions || [];
  const { pairs, singles } = groupPositions(positions);
  const errors = [];
  if (data?.exchanges?.darkex?.error)
    errors.push({ ex: "Darkex", msg: data.exchanges.darkex.error });
  if (data?.exchanges?.binance?.error)
    errors.push({ ex: "Binance", msg: data.exchanges.binance.error });

  const togglePanel = (key, type) =>
    setPanel(panel?.key === key && panel?.type === type ? null : { key, type });

  return (
    <div
      data-testid="exchange-positions-card"
      style={{
        border: "1px solid var(--line)",
        borderRadius: 14,
        background: "var(--panel)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        marginBottom: 20,
        overflow: "hidden",
      }}
    >
      <header
        style={{
          padding: "14px 18px",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "var(--acc-dim)",
              border: "1px solid var(--acc-line)",
              display: "grid",
              placeItems: "center",
              color: "var(--acc)",
            }}
          >
            <ExternalLink size={14} strokeWidth={1.7} />
          </span>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              Borsa Pozisyonları
              <span
                style={{
                  fontFamily: "var(--fm)",
                  fontSize: 10,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "var(--faint)",
                  marginLeft: 10,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid var(--line)",
                }}
                data-testid="exchange-positions-count"
              >
                {pairs.length} hedge · {singles.length} tekil
              </span>
            </h3>
            <div
              style={{
                fontFamily: "var(--fm)",
                fontSize: 10,
                color: "var(--faint)",
                marginTop: 3,
              }}
            >
              Darkex + Binance API ile canlı · kapatma/TP-SL/kaldıraç buradan yönetilir
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="exchange-positions-refresh"
          style={{ ...opBtn(), height: 32 }}
        >
          <RefreshCw
            size={12}
            className={isFetching ? "animate-spin" : ""}
          />
          Yenile
        </button>
      </header>

      {errors.length > 0 && (
        <div
          style={{
            padding: "10px 18px",
            background: "var(--gold-dim)",
            borderBottom: "1px solid var(--line)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontFamily: "var(--fm)",
            fontSize: 11,
            color: "var(--gold)",
          }}
          data-testid="exchange-positions-errors"
        >
          {errors.map((e) => (
            <div key={e.ex} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={12} />
              <b>{e.ex}:</b> <span style={{ color: "var(--muted)" }}>{e.msg}</span>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <Empty text="Pozisyonlar borsadan çekiliyor…" />
      ) : positions.length === 0 ? (
        <Empty
          testId="exchange-positions-empty"
          text={isError ? "Borsa verisi alınamadı." : "Hiçbir borsada açık pozisyon bulunamadı."}
        />
      ) : (
        <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          {pairs.map((pair) => (
            <PairCard
              key={`pair-${pair.dk.symbol}`}
              pair={pair}
              panel={panel}
              togglePanel={togglePanel}
              setPanel={setPanel}
              ops={{ closeLeg, closePair, tpsl, hedgeTpsl, leverage }}
            />
          ))}
          {singles.map((p, i) => (
            <SingleCard
              key={`single-${p.exchange}-${p.symbol}-${i}`}
              leg={p}
              panel={panel}
              togglePanel={togglePanel}
              setPanel={setPanel}
              ops={{ closeLeg, tpsl, leverage }}
            />
          ))}
        </div>
      )}

      {data && (
        <div
          style={{
            padding: "10px 18px",
            borderTop: "1px solid var(--line)",
            display: "flex",
            justifyContent: "space-between",
            fontFamily: "var(--fm)",
            fontSize: 11,
            color: "var(--faint)",
          }}
        >
          <span>{positions.length} açık bacak</span>
          <span
            data-testid="exchange-positions-total-upnl"
            style={{
              color: (data.total_unrealized_pnl || 0) >= 0 ? "var(--up)" : "var(--down)",
              fontWeight: 700,
            }}
          >
            Toplam uPnL {fmtSigned(data.total_unrealized_pnl)}
          </span>
        </div>
      )}
    </div>
  );
}

function Empty({ text, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        padding: 28,
        textAlign: "center",
        color: "var(--faint)",
        fontFamily: "var(--fm)",
        fontSize: 12,
      }}
    >
      {text}
    </div>
  );
}

/* ====================== HEDGE ÇİFTİ KARTI ====================== */
function PairCard({ pair, panel, togglePanel, setPanel, ops }) {
  const { dk, counter } = pair;
  const netPnl = Number(dk.unrealized_pnl || 0) + Number(counter.unrealized_pnl || 0);
  const pairKey = `${dk.symbol}-pair`;
  const counterOk = counter.exchange === "BINANCE";

  return (
    <div
      data-testid={`hedge-pair-${dk.symbol}`}
      style={{
        border: "1px solid var(--acc-line)",
        borderRadius: 12,
        background: "rgba(255,93,21,0.03)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Link2 size={14} style={{ color: "var(--acc)" }} />
          <b style={{ fontSize: 14 }}>{dk.symbol}</b>
          <span
            style={{
              fontFamily: "var(--fm)",
              fontSize: 9,
              letterSpacing: "0.14em",
              padding: "2px 8px",
              borderRadius: 999,
              background: "var(--acc-dim)",
              border: "1px solid var(--acc-line)",
              color: "var(--acc)",
              fontWeight: 700,
            }}
          >
            HEDGE ÇİFTİ
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontFamily: "var(--fm)", fontSize: 10, color: "var(--faint)" }}>
            NET uPnL
          </span>
          <b
            data-testid={`pair-net-upnl-${dk.symbol}`}
            style={{
              fontFamily: "var(--fm)",
              fontSize: 14,
              color: netPnl >= 0 ? "var(--up)" : "var(--down)",
            }}
          >
            {fmtSigned(netPnl)}
          </b>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 0,
        }}
      >
        <LegBox leg={dk} panel={panel} togglePanel={togglePanel} ops={ops} bordered />
        <LegBox leg={counter} panel={panel} togglePanel={togglePanel} ops={ops} />
      </div>

      {counterOk && (
        <div
          style={{
            padding: "10px 14px",
            borderTop: "1px solid var(--line)",
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            style={opBtn("accent")}
            onClick={() => togglePanel(pairKey, "hedge")}
            data-testid={`pair-hedge-tpsl-btn-${dk.symbol}`}
          >
            ⚖ Hedge TP/SL (eşit tetik)
          </button>
          <ConfirmButton
            label="✕ Çifti Kapat (iki borsa aynı anda)"
            busy={ops.closePair.isPending}
            testId={`pair-close-btn-${dk.symbol}`}
            onConfirm={() =>
              ops.closePair.mutate({
                symbol: dk.symbol,
                darkex_side: dk.side,
                darkex_quantity: dk.size,
                counter_exchange: counter.exchange,
                counter_side: counter.side,
                counter_quantity: counter.size,
              })
            }
          />
        </div>
      )}

      {panel?.key === pairKey && panel?.type === "hedge" && (
        <HedgeTpslPanel
          pair={pair}
          busy={ops.hedgeTpsl.isPending}
          onClose={() => setPanel(null)}
          onSubmit={(price) =>
            ops.hedgeTpsl.mutate({
              symbol: dk.symbol,
              trigger_price: price,
              darkex_side: dk.side,
              counter_exchange: counter.exchange,
              counter_side: counter.side,
            })
          }
        />
      )}
      <PanelHost legs={[dk, counter]} panel={panel} setPanel={setPanel} ops={ops} />
    </div>
  );
}

/* ====================== TEKİL BACAK KARTI ====================== */
function SingleCard({ leg, panel, togglePanel, setPanel, ops }) {
  return (
    <div
      data-testid={`single-leg-${leg.exchange}-${leg.symbol}`}
      style={{
        border: "1px solid var(--line)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <LegBox leg={leg} panel={panel} togglePanel={togglePanel} ops={ops} single />
      <PanelHost legs={[leg]} panel={panel} setPanel={setPanel} ops={ops} />
    </div>
  );
}

/* Bir bacağın açık panelini (TP/SL veya kaldıraç) render eder. */
function PanelHost({ legs, panel, setPanel, ops }) {
  if (!panel) return null;
  const leg = legs.find((l) => panel.key === `${l.symbol}-${l.exchange}`);
  if (!leg) return null;
  if (panel.type === "tpsl") {
    return (
      <TpslPanel
        leg={leg}
        busy={ops.tpsl.isPending}
        onClose={() => setPanel(null)}
        onSubmit={(vals) =>
          ops.tpsl.mutate({
            exchange: leg.exchange,
            symbol: leg.symbol,
            side: leg.side,
            ...vals,
          })
        }
      />
    );
  }
  if (panel.type === "lev") {
    return (
      <LeveragePanel
        leg={leg}
        busy={ops.leverage.isPending}
        onClose={() => setPanel(null)}
        onSubmit={(lev) =>
          ops.leverage.mutate({
            exchange: leg.exchange,
            symbol: leg.symbol,
            leverage: lev,
          })
        }
      />
    );
  }
  return null;
}

/* ====================== BACAK KUTUSU ====================== */
function LegBox({ leg, togglePanel, ops, bordered, single }) {
  const long = leg.side === "LONG";
  const profit = (leg.unrealized_pnl || 0) >= 0;
  const key = `${leg.symbol}-${leg.exchange}`;
  const opsSupported = leg.exchange === "DARKEX" || leg.exchange === "BINANCE";

  const cell = (label, value, color) => (
    <div>
      <div
        style={{
          fontSize: 9,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--faint)",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 12, color: color || "var(--txt)", fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );

  return (
    <div
      style={{
        padding: "12px 14px",
        fontFamily: "var(--fm)",
        ...(bordered ? { borderRight: "1px solid var(--line)" } : {}),
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <b style={{ color: exColor(leg.exchange), fontSize: 12 }}>
            {exLabel(leg.exchange)}
          </b>
          {single && <b style={{ fontSize: 12 }}>{leg.symbol}</b>}
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 10,
              background: long ? "var(--up-dim)" : "var(--down-dim)",
              color: long ? "var(--up)" : "var(--down)",
            }}
          >
            {long ? "▲ Long" : "▼ Short"}
          </span>
          <span style={{ fontSize: 10, color: "var(--faint)" }}>
            {leg.leverage ? `${leg.leverage}×` : ""}{" "}
            {leg.margin_type === "isolated" ? "izole" : leg.margin_type || ""}
          </span>
        </div>
        <b
          style={{
            fontSize: 13,
            color: profit ? "var(--up)" : "var(--down)",
          }}
          data-testid={`leg-upnl-${leg.exchange}-${leg.symbol}`}
        >
          {fmtSigned(leg.unrealized_pnl)}
        </b>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(70px, 1fr))",
          gap: 8,
          marginBottom: 10,
        }}
      >
        {cell("Miktar", fmtNum(leg.size, 4))}
        {cell("Giriş", `$${fmtNum(leg.entry_price)}`)}
        {cell("Mark", `$${fmtNum(leg.mark_price)}`, "var(--muted)")}
        {cell(
          "Liq.",
          leg.liquidation_price ? `$${fmtNum(leg.liquidation_price)}` : "—",
          "var(--faint)"
        )}
      </div>

      {opsSupported && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            type="button"
            style={opBtn()}
            onClick={() => togglePanel(key, "tpsl")}
            data-testid={`leg-tpsl-btn-${leg.exchange}-${leg.symbol}`}
          >
            TP/SL
          </button>
          <button
            type="button"
            style={opBtn()}
            onClick={() => togglePanel(key, "lev")}
            data-testid={`leg-leverage-btn-${leg.exchange}-${leg.symbol}`}
          >
            Kaldıraç
          </button>
          <ConfirmButton
            label="Kapat"
            busy={ops.closeLeg.isPending}
            testId={`leg-close-btn-${leg.exchange}-${leg.symbol}`}
            onConfirm={() =>
              ops.closeLeg.mutate({
                exchange: leg.exchange,
                symbol: leg.symbol,
                side: leg.side,
                quantity: leg.size,
              })
            }
          />
        </div>
      )}
    </div>
  );
}
